import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * SCRUM-2261 — in-car text-size control.
 *
 * Renders a small A− / A+ widget ONLY when car mode is active (the `tesla`
 * class is on <html>, set pre-boot in main.ts). It adjusts `--car-scale` — the
 * variable `html.tesla { font-size }` reads — and persists the choice to
 * `hub_car_scale`, so the user tunes the whole-app size themselves once and it
 * sticks across reloads. This ends the "too small → too large" guessing on a
 * fixed number. Presentation-only.
 */
@Component({
  selector: 'app-car-mode-size',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div *ngIf="isCar" class="cms" [class.cms-car-native]="carNative" role="group" aria-label="Car text size" data-testid="car-size-control">
      <button type="button" class="cms-btn" (click)="step(-STEP)" [disabled]="scale <= MIN" aria-label="Smaller text">A−</button>
      <span class="cms-val">{{ scale }}%</span>
      <button type="button" class="cms-btn" (click)="step(STEP)" [disabled]="scale >= MAX" aria-label="Larger text">A+</button>
    </div>
  `,
  styles: [`
    .cms {
      position: fixed;
      right: 12px;
      bottom: 12px;
      z-index: 9999;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 10px;
      border-radius: 999px;
      background: var(--surface-0, #fff);
      border: 1px solid var(--surface-border, #e0e0e0);
      box-shadow: 0 4px 14px rgba(0,0,0,0.18);
    }
    .cms-btn {
      min-width: 44px;
      min-height: 44px;
      border-radius: 999px;
      border: 1px solid var(--surface-border, #e0e0e0);
      background: var(--surface-50, #fafafa);
      color: var(--text-color, #1f2937);
      font-weight: 700;
      font-size: 1.1rem;
      cursor: pointer;
    }
    /* RDS-16 — on the car-native screens every target is ≥72px (CAR_MODE_REDESIGN_EPIC). */
    .cms-car-native { top: 12px; bottom: auto; }
    .cms-car-native .cms-btn { min-width: 72px; min-height: 72px; }
    .cms-btn:disabled { opacity: 0.4; cursor: default; }
    .cms-val {
      min-width: 3.2rem;
      text-align: center;
      font-weight: 600;
      font-size: 0.95rem;
      color: var(--text-color-secondary, #6b7280);
    }
  `],
})
export class CarModeSizeComponent {
  /** RDS-16 — true on the car-native screens: buttons grow to the 72px car target. */
  @Input() carNative = false;
  readonly MIN = 100;
  readonly MAX = 175;
  readonly STEP = 5;
  private readonly DEFAULT = 125;

  isCar = false;
  scale = this.DEFAULT;

  constructor() {
    try {
      this.isCar = document.documentElement.classList.contains('tesla');
      const s = Number(localStorage.getItem('hub_car_scale'));
      if (Number.isFinite(s) && s >= this.MIN && s <= this.MAX) this.scale = s;
    } catch { /* SSR / storage unavailable */ }
  }

  step(delta: number): void {
    this.scale = Math.min(this.MAX, Math.max(this.MIN, this.scale + delta));
    try {
      localStorage.setItem('hub_car_scale', String(this.scale));
      document.documentElement.style.setProperty('--car-scale', `${this.scale}%`);
    } catch { /* storage unavailable */ }
  }
}
